import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Trophy, Users, Calendar, FileText, Bell, Settings, Download, Star, CheckCircle, ArrowRight, Mail, Phone, MessageCircle, Facebook } from "lucide-react";

const SECTIONS = [
  {
    id: "inizio",
    icon: Star,
    color: "from-emerald-600 to-emerald-700",
    badge: "01",
    title: "Inizia con Tabellone Facile",
    subtitle: "Registrazione, accesso e primo accesso alla piattaforma",
    steps: [
      {
        title: "Crea il tuo account",
        content: "Vai su tabellonefacile.net e clicca su Registrati. Puoi usare la tua email e una password, oppure accedere direttamente con Google o Facebook con un solo clic. Riceverai un'email di conferma: clicca il link per attivare l'account.",
        tip: "Hai 14 giorni di prova gratuita con accesso completo. Nessuna carta di credito richiesta. Durante la prova: 1 torneo, 4 categorie e 15 giocatori per categoria."
      },
      {
        title: "Conosci Coach Bruno (il tuo assistente)",
        content: "In basso a destra trovi sempre Coach Bruno, l'assistente virtuale a forma di tennista. Cliccalo per aprire il chatbot: puoi scegliere tra le opzioni rapide oppure digitare la tua domanda nel campo testo. Coach Bruno conosce tutta la piattaforma e ti guida passo-passo.",
        tip: "Coach Bruno e' sempre disponibile su tutte le pagine. Le sue risposte sono istantanee e gratuite."
      },
      {
        title: "Configura il tuo Circolo (Info Circolo)",
        content: "Dal menu utente in alto a destra, scegli Info Circolo. Inserisci il nome del circolo, l'indirizzo completo, telefono ed email. Carica il logo del Circolo (verra' stampato in alto a sinistra in tutti i PDF) e aggiungi loghi extra come FITP, ATP, WTA o sponsor (compariranno in alto a destra).",
        tip: "Il link Google Maps trasforma l'indirizzo in un link cliccabile sui PDF: utile per i giocatori che ricevono il calendario via email. Per eliminare un logo: pulsante rosso ✕ sempre visibile."
      }
    ]
  },
  {
    id: "torneo",
    icon: Trophy,
    color: "from-emerald-700 to-teal-700",
    badge: "02",
    title: "Crea e Gestisci un Torneo",
    subtitle: "Dalla creazione al setup di campi e fasce orarie",
    steps: [
      {
        title: "Crea un nuovo torneo",
        content: "Dalla Home clicca su + Nuovo Torneo. Inserisci nome, date di inizio e fine, numero di campi disponibili e la fascia oraria predefinita (Pomeriggio → Sera → Mattina di default). Salva: il torneo nasce vuoto e pronto per essere configurato.",
        tip: "Le fasce orarie sono: Mattina 09-13, Pomeriggio 13-17, Sera 17-21. L'ordine determina la priorita' di riempimento del calendario."
      },
      {
        title: "Imposta i parametri operativi",
        content: "Nel tab Dettagli del torneo trovi tre box: 1) Impostazioni Generali con Max Incontri per Giocatore/Giorno e Tempo di Riposo (in minuti); 2) Importa Giocatori (separato); 3) Notifiche Email.",
        tip: "Il tempo di riposo di default si applica a tutti i giocatori ma resta modificabile per ogni singolo giocatore nella scheda Categorie."
      },
      {
        title: "Disponibilita' Campi e finestre orarie",
        content: "Nel tab Dettagli, sezione Disponibilita' Campi, configura per ogni giorno: quanti campi sono attivi, i loro nomi e le eventuali finestre di disponibilita' (es. dalle 14:30 alle 21:30). Puoi anche impostare una disponibilita' Globale (tutti i giorni) o Default (per gli altri giorni).",
        tip: "Se imposti una finestra 14:30-21:30 il sistema NON pianifica partite fuori da quell'orario, neanche se ci sono slot teorici disponibili."
      },
      {
        title: "Aggiungi le categorie",
        content: "Nel tab Categorie clicca + Aggiungi Categoria. Indica nome (es. Singolare U14 Maschile), durata media di una partita e tipo di tabellone. Un torneo puo' avere piu' categorie indipendenti, ognuna con le proprie regole.",
        tip: "Durante la prova hai un massimo di 4 categorie. Con un abbonamento attivo, illimitate."
      }
    ]
  },
  {
    id: "giocatori",
    icon: Users,
    color: "from-sky-700 to-blue-700",
    badge: "03",
    title: "Gestione Giocatori",
    subtitle: "Import Excel e preferenze orarie giornaliere",
    steps: [
      {
        title: "Importa giocatori da Excel",
        content: "Nel tab Dettagli, riquadro Importa Giocatori, clicca Scarica Template per ottenere il file Excel. Compila le 9 colonne: 1) Categoria, 2) Cognome e Nome, 3) Sesso (M/F), 4) Cat. Eta', 5) Ranking, 6) Circolo, 7) Email, 8) Telefono, 9) Preferenze Orario. Salva come .xlsx e ricaricalo cliccando Carica File Excel.",
        tip: "Le classifiche FITP valide: 1.1-1.6, 2.1-2.8, 3.1-3.6, 4.1-4.6, 4.NC. Se una categoria nel file Excel non esiste ancora, viene creata automaticamente."
      },
      {
        title: "Aggiungi giocatori manualmente",
        content: "Nella scheda Categorie → Giocatori, clicca + Aggiungi Giocatore. Compila nome, ranking, sesso, eta', email, telefono. Puoi anche impostare il tempo di riposo individuale (se diverso dal default).",
        tip: "L'icona matita ✏ accanto a ogni nome del tabellone e' sempre visibile per modifiche rapide al volo."
      },
      {
        title: "Preferenze orarie per giocatore",
        content: "Nella modifica di un giocatore, sezione Preferenze Orario, trovi una griglia 7 giorni × 3 fasce (9-13 / 13-17 / 17-21). Clicca i quadrati per attivare/disattivare le fasce in cui il giocatore puo' giocare. SHORTCUT: clicca il label di una fascia (es. 13-17) per applicarla a TUTTI i giorni in un colpo.",
        tip: "Se non imposti preferenze, il giocatore viene considerato disponibile in tutte le fasce. La schedulazione rispetta queste preferenze."
      }
    ]
  },
  {
    id: "tabellone",
    icon: Settings,
    color: "from-teal-700 to-cyan-700",
    badge: "04",
    title: "Genera il Tabellone",
    subtitle: "4 modalita' di generazione conformi al Regolamento FITP",
    steps: [
      {
        title: "Quattro modalita' disponibili",
        content: "Nel tab Tabellone della categoria, in toolbar trovi: CREA VUOTO (solo slot da riempire manualmente), CASUALE (distribuzione casuale), CON TDS (selezione manuale delle Teste di Serie con frecce di riordino), PER RANKING (divisione automatica in sezioni 4.NC, 4.x, 3.x, 2.x, 1.x).",
        tip: "Tutti i pulsanti sono dello stesso colore verde per coerenza visiva. Il pulsante PDF e Vista Completa stanno in una seconda riga sotto."
      },
      {
        title: "Modalita' PER RANKING (consigliata FITP)",
        content: "Clicca Per Ranking: l'algoritmo divide i giocatori per fascia di ranking, crea un sotto-tabellone per ogni sezione e fa avanzare N qualificati da una sezione all'altra. E' la modalita' piu' aderente al Regolamento FITP per tornei multi-fascia.",
        tip: "Per ogni sezione puoi impostare quanti qualificati avanzano (default 2). Il pulsante popup ha lo stesso layout verde di Con TDS."
      },
      {
        title: "Modalita' CON TDS (manuale)",
        content: "Clicca Con TdS: per ogni sezione vedi tutti i giocatori e puoi selezionare manualmente le TdS. Usa le frecce su/giu' per riordinare (TdS 1 in cima, TdS 2 sotto, ecc.). Imposta anche il N° Qualificati per sezione e clicca GENERA TABELLONE.",
        tip: "Le TdS vengono piazzate nelle posizioni geometriche standard FITP: S1 in posizione 1, S2 in fondo, S3-S4 in metà opposte, S5-S8 nei 4 quarti."
      },
      {
        title: "Conformita' al Regolamento FITP",
        content: "Tabellone Facile applica automaticamente: distribuzione BYE ai seed in ordine, qualificati in aspèttito contro TDS numerazione piu' elevata (Vol. II pag. 7 NB), favore di pronostico, regola 7.2.4 sul posizionamento qualificato (riga superiore in meta' inferiore e viceversa). Sul PDF compare il badge 'Tabellone generato secondo Regolamento FITP'.",
        tip: "L'algoritmo evita doppi BYE allo stesso giocatore e separa automaticamente le TDS in meta' opposte del tabellone."
      },
      {
        title: "Eliminare il tabellone (doppia conferma)",
        content: "Click su ELIMINA (pulsante rosso): apparira' una 1° conferma. Click su Conferma → apparira' una 2° conferma rossa 'Sei davvero sicuro?' con il testo dell'azione irreversibile. Solo dopo questa seconda conferma il tabellone viene cancellato definitivamente.",
        tip: "L'eliminazione cancella anche il calendario e tutti i risultati gia' inseriti. Non e' recuperabile: sei doppiamente avvisato."
      }
    ]
  },
  {
    id: "calendario",
    icon: Calendar,
    color: "from-cyan-700 to-sky-700",
    badge: "05",
    title: "Pianifica il Calendario",
    subtitle: "Generazione automatica + modifica/eliminazione singola",
    steps: [
      {
        title: "Genera il calendario automatico",
        content: "Nel tab Calendario, clicca GENERA CALENDARIO. L'algoritmo distribuisce tutte le partite rispettando: max incontri/giorno per giocatore, tempo di riposo minimo, disponibilita' campi per giorno, finestre orarie configurate, priorita' delle fasce, ordine logico dei turni (R64 prima di R32 ecc.).",
        tip: "Se una partita non ha i giocatori certi (es. aspetta vincitore di R64-3), viene pianificata SEMPRE dopo i suoi 'feeder' per garantire sequenza logica."
      },
      {
        title: "Drag & Drop per spostare partite",
        content: "Tieni premuta una partita nel calendario e trascinala su un altro giorno: il sistema aggiorna la programmazione e ricalcola eventuali conflitti. Le partite con conflitto vengono evidenziate in rosso.",
        tip: "Il drag&drop e' utile per piccoli aggiustamenti manuali. Per modifiche piu' precise usa la dialog Modifica."
      },
      {
        title: "Modifica una singola partita",
        content: "Clicca su una partita per aprire la dialog Modifica. Nel tab Programmazione puoi cambiare data, orario di inizio, orario di fine (la durata si calcola automaticamente), campo. Nel tab Risultato puoi inserire il punteggio set per set e dichiarare il vincitore.",
        tip: "L'orario di fine - inizio definisce la durata. Se inserisci 14:30 inizio e 15:45 fine, la durata sara' 75 minuti automaticamente."
      },
      {
        title: "Elimina la programmazione di una sola partita",
        content: "Sempre nella dialog Modifica, sotto al pulsante Salva Programmazione c'e' Elimina questa partita dal calendario (pulsante rosso outline). Click → conferma → la partita torna nello stato 'da programmare' senza perdere risultato e bracket position.",
        tip: "Utile per liberare uno slot specifico senza rigenerare l'intero calendario."
      },
      {
        title: "Elimina tutto il calendario (doppia conferma)",
        content: "Nel tab Calendario click ELIMINA (pulsante rosso) → 1° conferma → 2° conferma rossa 'Sei davvero sicuro?'. Tutte le programmazioni vengono cancellate ma i risultati delle partite gia' giocate non vengono toccati.",
        tip: "Puoi rigenerare il calendario tutte le volte che vuoi. La doppia conferma previene cancellazioni accidentali."
      }
    ]
  },
  {
    id: "notifiche",
    icon: Bell,
    color: "from-blue-700 to-violet-700",
    badge: "06",
    title: "Notifiche Email",
    subtitle: "Promemoria automatici e cambi orario",
    steps: [
      {
        title: "Notifiche su cambio programmazione",
        content: "Nel tab Dettagli del torneo, riquadro Notifiche Email, attiva la spunta 'Invia email su cambio programmazione'. Ogni volta che modifichi orario, campo o data di una partita, i giocatori coinvolti ricevono un'email automatica con i nuovi dettagli.",
        tip: "Le email vengono inviate solo ai giocatori che hanno un indirizzo email registrato nel sistema."
      },
      {
        title: "Promemoria prima del match (intervalli configurabili)",
        content: "Nello stesso riquadro Notifiche Email, sezione 'Promemoria prima del match', aggiungi gli intervalli che ti servono (in ore). Ad esempio: 48h, 24h, 2h. Clicca + Aggiungi promemoria per creare nuovi intervalli, X per rimuoverli.",
        tip: "I promemoria sono cross-categorie: se un giocatore e' iscritto in piu' categorie, riceve un promemoria per ogni partita."
      }
    ]
  },
  {
    id: "pdf",
    icon: Download,
    color: "from-violet-700 to-purple-700",
    badge: "07",
    title: "Esportazione PDF",
    subtitle: "Tabellone, calendario, PDF con tutti i loghi e note personalizzate",
    steps: [
      {
        title: "PDF Tabellone con tutti i dettagli",
        content: "Nel tab Tabellone, sotto al menu categorie, clicca PDF. Il PDF include: header con logo circolo (sinistra) e loghi extra FITP/sponsor (destra), nome torneo e categoria, badge 'Tabellone generato secondo Regolamento FITP', struttura ad albero con TdS, risultati con (W) accanto al vincitore + punteggio, durata match sotto il box.",
        tip: "I box dei giocatori sono ora piu' bassi per stampare piu' partite per pagina. Vista Completa apre invece la versione interattiva nel browser."
      },
      {
        title: "PDF Calendario (2 formati)",
        content: "Nel tab Calendario hai due pulsanti: PDF (formato tabellare per giorno con colonne Ora/Campo/Categoria/Turno/Giocatore1/vs/Giocatore2/Punteggio/Durata) e PDF CON TUTTI I LOGHI (formato ufficiale FITP-style con un layout a celle, una per match, con loghi sponsor in evidenza).",
        tip: "Usa il PDF standard per uso interno e operativo; usa il PDF con tutti i loghi per esposizione in bacheca o invio alle istituzioni."
      },
      {
        title: "Note personalizzate in fondo a ogni pagina",
        content: "Sia nel Tabellone che nel Calendario c'e' il pulsante Note PDF: clicca per aprire la dialog e scrivere un messaggio (es. 'Inizio incontri ore 9:00 — Info: +39 ...'). La nota verra' stampata in fondo a OGNI pagina del PDF con una linea separatoria e il label 'NOTE:'.",
        tip: "Le note sono limitate a circa 4 righe per non occupare troppo spazio. Le salvi una volta e restano memorizzate fino a modifica."
      }
    ]
  },
  {
    id: "abbonamento",
    icon: FileText,
    color: "from-purple-700 to-emerald-700",
    badge: "08",
    title: "Piani e Abbonamenti",
    subtitle: "Prova gratuita di 14 giorni e piani Stripe",
    steps: [
      {
        title: "Prova gratuita di 14 giorni",
        content: "Ogni nuovo account ha 14 giorni di prova gratuita senza carta di credito. Limiti: 1 torneo, 4 categorie per torneo, 15 giocatori per categoria. Tutte le altre funzionalita' (PDF, notifiche, drag&drop, FITP compliance...) sono attive.",
        tip: "Se raggiungi un limite, appare una finestra arancio con la spiegazione del limite e un CTA 'Attiva abbonamento'."
      },
      {
        title: "Scegli il tuo piano",
        content: "Dal menu utente in alto a destra, clicca Il mio Abbonamento. Scegli tra piani da 1 mese, 3 mesi, 6 mesi, 9 mesi o annuale. Il pagamento avviene in modo sicuro tramite Stripe (carta di credito/debito).",
        tip: "Con un abbonamento attivo: tornei, categorie e giocatori illimitati. Funzionalita' PDF avanzate sempre attive."
      }
    ]
  },
  {
    id: "contatti",
    icon: MessageCircle,
    color: "from-emerald-700 to-emerald-900",
    badge: "09",
    title: "Contatti e Supporto",
    subtitle: "Scrivici, chiamaci o seguici sui social",
    steps: [
      {
        title: "Email",
        content: "Scrivici a info@tabellonefacile.net per richieste di supporto, dubbi commerciali, segnalazioni bug o suggerimenti. Risposta entro 24h nei giorni lavorativi.",
        tip: "Indica sempre nome torneo e descrizione precisa dell'eventuale problema."
      },
      {
        title: "Telefono e WhatsApp",
        content: "Chiamaci o scrivici su WhatsApp al +39 376 289 1321. Disponibili anche durante i weekend dei tornei per supporto urgente.",
        tip: "WhatsApp e' il canale piu' veloce: rispondiamo solitamente entro pochi minuti."
      },
      {
        title: "Facebook",
        content: "Seguici sulla pagina Facebook ufficiale per news, aggiornamenti sulle nuove feature e tornei in evidenza. Link: facebook.com/profile.php?id=61575382990256",
        tip: "Iscriviti per ricevere notifiche sulle nuove funzionalita' rilasciate."
      }
    ]
  }
];

function StepCard({ step, index }) {
  return (
    <div className="flex gap-4 p-5 rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center text-emerald-700 font-bold text-sm">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-slate-800 mb-1">{step.title}</h4>
        <p className="text-slate-600 text-sm leading-relaxed mb-3">{step.content}</p>
        {step.tip && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="text-amber-600 text-xs font-bold mt-0.5 flex-shrink-0">💡 CONSIGLIO</span>
            <p className="text-amber-800 text-xs leading-relaxed">{step.tip}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionCard({ section }) {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;

  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-6 text-left hover:bg-slate-50 transition-colors"
        data-testid={`guide-section-${section.id}`}
      >
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center flex-shrink-0`}>
          <Icon size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-bold text-slate-400 tracking-widest">{section.badge}</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800 leading-tight">{section.title}</h3>
          <p className="text-sm text-slate-500 mt-0.5">{section.subtitle}</p>
        </div>
        <div className="flex-shrink-0 ml-2">
          {open
            ? <ChevronDown size={20} className="text-slate-400" />
            : <ChevronRight size={20} className="text-slate-400" />
          }
        </div>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-3 bg-slate-50 border-t border-slate-100">
          <div className="pt-4 space-y-3">
            {section.steps.map((step, i) => (
              <StepCard key={i} step={step} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GuidaPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = searchQuery.trim().length > 1
    ? SECTIONS.filter(s =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.steps.some(st =>
          st.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          st.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : SECTIONS;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-800 to-teal-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <CheckCircle size={14} />
            Guida completa alla piattaforma
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Come usare<br />Tabellone Facile
          </h1>
          <p className="text-emerald-100 text-lg max-w-xl mx-auto mb-8">
            Tutto quello che devi sapere per organizzare i tuoi tornei di tennis in modo professionale.
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <input
              type="text"
              placeholder="Cerca nelle istruzioni..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              data-testid="guide-search-input"
              className="w-full px-5 py-3 pl-12 rounded-xl text-slate-800 bg-white shadow-lg placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
            />
            <svg className="absolute left-4 top-3.5 text-slate-400" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Quick nav pills */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-2.5 flex gap-2 overflow-x-auto scrollbar-hide">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-xs font-medium text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
              >
                <Icon size={12} />
                {s.title.split(" ").slice(0, 2).join(" ")}
              </a>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-4">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <p className="text-lg font-medium">Nessun risultato per "{searchQuery}"</p>
            <p className="text-sm mt-1">Prova con un'altra parola chiave</p>
          </div>
        )}
        {filtered.map(section => (
          <div key={section.id} id={section.id}>
            <SectionCard section={section} />
          </div>
        ))}
      </div>

      {/* Footer CTA + Contatti */}
      <div className="bg-slate-50 border-t border-slate-200 mt-8">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Hai ancora domande?
            </h2>
            <p className="text-slate-500 mb-6">
              Contattaci o accedi alla piattaforma per iniziare subito con la prova gratuita di 14 giorni.
            </p>
            <button
              onClick={() => navigate("/")}
              data-testid="guide-cta-btn"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              Vai alla piattaforma
              <ArrowRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <a
              href="mailto:info@tabellonefacile.net"
              data-testid="guide-contact-email"
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-emerald-400 hover:shadow-md transition-all group"
            >
              <div className="h-10 w-10 rounded-full bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                <Mail className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400">Email</div>
                <div className="text-sm font-medium text-slate-800 truncate">info@tabellonefacile.net</div>
              </div>
            </a>
            <a
              href="https://wa.me/393762891321"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="guide-contact-whatsapp"
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-emerald-400 hover:shadow-md transition-all group"
            >
              <div className="h-10 w-10 rounded-full bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                <Phone className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400">Telefono / WhatsApp</div>
                <div className="text-sm font-medium text-slate-800">+39 376 289 1321</div>
              </div>
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61575382990256"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="guide-contact-facebook"
              className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-blue-400 hover:shadow-md transition-all group"
            >
              <div className="h-10 w-10 rounded-full bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                <Facebook className="h-5 w-5 text-blue-700" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400">Facebook</div>
                <div className="text-sm font-medium text-slate-800">Tabellone Facile</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
